// The number and date formats of the chart Components. They act as tokens for numbers: every
// chart, tile and table shows a value in the same way.
// Money: the app format (Utilities.formatPrice): the symbol before the number, en-US groups,
// no decimals by default. Examples: €142, €1,240, -€12.
// Percent: the value is a fraction. 0.714 → 71%, or 71.4% with one decimal.
// A stay date is an ISO date (YYYY-MM-DD). The formats read it in UTC, so a date never moves
// one day back west of UTC.
// This file knows nothing about RoomPulse data.

export type ValueFormat = 'money' | 'percent' | 'number';

export type FormatOptions = {
	// An ISO 4217 code for 'money', for example 'EUR'. Default 'EUR'.
	currency?: string,
	// The number of decimals. Default 0.
	decimals?: number,
};

// The text for a missing value.
export const NO_VALUE = '–';

const moneyFormats = new Map<string, Intl.NumberFormat>();
const moneyFormat = (currency: string, decimals: number): Intl.NumberFormat => {
	const key = `${currency}/${decimals}`;
	let format = moneyFormats.get(key);
	if (format === undefined) {
		format = new Intl.NumberFormat('en-US', {
			style: 'currency', currency, currencyDisplay: 'narrowSymbol',
			minimumFractionDigits: decimals, maximumFractionDigits: decimals,
		});
		moneyFormats.set(key, format);
	}
	return format;
};

export const formatMoney = (value: number, currency = 'EUR', decimals = 0): string =>
	moneyFormat(currency, decimals).format(value);

export const formatPercent = (fraction: number, decimals = 0): string =>
	(fraction * 100).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + '%';

export const formatNumber = (value: number, decimals = 0): string =>
	value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

export const formatValue = (value: number | null | undefined, format: ValueFormat, options: FormatOptions = {}): string => {
	if (value === null || value === undefined || Number.isNaN(value)) return NO_VALUE;
	const decimals = options.decimals ?? 0;
	if (format === 'money') return formatMoney(value, options.currency ?? 'EUR', decimals);
	if (format === 'percent') return formatPercent(value, decimals);
	return formatNumber(value, decimals);
};

// A value with its sign: +€12, -€8, +3.5%. Zero has no sign.
export const formatSigned = (value: number | null | undefined, format: ValueFormat, options: FormatOptions = {}): string => {
	const text = formatValue(value, format, options);
	return value !== null && value !== undefined && value > 0 ? `+${text}` : text;
};

// A difference of two fractions in percentage points: 0.023 → 2.3 pts. With signed, + before a positive value.
// The value is rounded first, so a difference that rounds to 0 shows 0.0 pts, with no sign.
export const roundPoints = (value: number, decimals = 1): number => {
	const rounded = Number((value * 100).toFixed(decimals)) / 100;
	return rounded === 0 ? 0 : rounded;
};
export const formatPoints = (value: number, decimals = 1, signed = false): string => {
	const rounded = roundPoints(value, decimals);
	return `${signed && rounded > 0 ? '+' : ''}${formatNumber(rounded * 100, decimals)} pts`;
};

// The kind of a change between two values.
// percent: a relative change as a fraction, 0.061 → 6.1%.
// points: a difference of two fractions in percentage points, 0.023 → 2.3 pts.
// money, number: a plain difference, 12 → €12.
export type ChangeFormat = 'percent' | 'points' | 'money' | 'number';

// The size of a change, without a sign: an arrow or a word gives the direction.
// Default decimals: 1 for percent and points, 0 for money and number.
export const formatChange = (value: number, format: ChangeFormat, options: FormatOptions = {}): string => {
	const size = Math.abs(value);
	if (format === 'points') return formatPoints(size, options.decimals ?? 1);
	if (format === 'percent') return formatPercent(size, options.decimals ?? 1);
	return formatValue(size, format, options);
};

// The ms of the UTC midnight of an ISO date (YYYY-MM-DD, or a longer ISO text).
export const stayDateMs = (iso: string): number => {
	const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
	return Date.UTC(y, m - 1, d);
};

const shortDate = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const longDate = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

// 12 Oct
export const formatStayDate = (iso: string): string => shortDate.format(stayDateMs(iso));

// Mon 12 Oct 2026
export const formatStayDateLong = (iso: string): string => longDate.format(stayDateMs(iso)).replace(',', '');
